import React from 'react';
import { Row, Col, Form, Select, FormInstance } from 'antd';
import { Colors } from '@common/tokens';
import { Currency } from '@common/components';
import { ExtensionReviewChanges } from './extension-review-changes';
import { BillingCardDeviationAlert } from 'works/legacy/components/billing-card-deviation-alert';
import { getRateByEmploymentType } from '@institutions/services';
import { ExtensionOfferValues } from 'works/legacy/components/extension-offer/extension-offer-form';
import { AssignmentType } from 'works/legacy/components/extension-offer/create-extension-offer-flow';
import { Store } from 'antd/lib/form/interface';
import { DrawerInfoRow } from '@working-experience/components/drawer-info-row';
import { getLengthInWeeksInclusiveValue } from 'works/legacy/services/length-in-weeks-service';
import {
  EXTENSION_OFFER_REASON_KEYS,
  getExtensionOfferReasonDescription,
} from '@working-experience/services';

const { Option } = Select;

type ExtensionOfferReviewProps = {
  assignment: AssignmentType;
  newData: ExtensionOfferValues;
  onFinish: (values: Store) => void;
  form: FormInstance;
};

export function ExtensionOfferReview({
  assignment,
  newData,
  onFinish,
  form,
}: ExtensionOfferReviewProps) {
  const billingCard = newData.billingCard;
  const hasBillingCard = !!billingCard;
  const rateCardRateValue = getRateByEmploymentType(
    billingCard,
    newData.shiftType,
    assignment.employmentType
  );
  const showDeviationWarning = hasBillingCard && newData.deviated;
  const weeksDiff = getLengthInWeeksInclusiveValue(
    newData.startDate,
    newData.endDate
  );

  const hoursPerWeek = assignment.hoursPerShift * assignment.shiftsPerWeek;
  const totalHours = assignment.lengthInWeeks * hoursPerWeek;
  const initialBudget = totalHours * assignment.billingRegularRate;

  const budgetIncrement = hoursPerWeek * weeksDiff * newData.billingRegularRate;

  const differenceRate =
    newData.billingRegularRate - assignment.billingRegularRate;

  const rateCardDifferenceRate =
    newData.billingRegularRate - (rateCardRateValue || 0);

  return (
    <div className="ExtensionOfferReview">
      <DrawerInfoRow
        label="Extension Start Date"
        info={newData.startDate.format('MM/DD/YYYY')}
      />
      <DrawerInfoRow
        label="Extension End Date"
        info={
          <>
            {newData.endDate.format('MM/DD/YYYY')} ({weeksDiff} week
            {weeksDiff > 1 ? 's' : ''})
          </>
        }
      />
      <div style={{ marginBottom: 16 }} />
      {showDeviationWarning && (
        <ExtensionReviewChanges
          beforeTitle="Rate Card Rate"
          beforeComponent={<Currency value={rateCardRateValue || 0} />}
          forwardComponent={
            <>
              <br />
              {rateCardDifferenceRate > 0 ? '+' : ''}
              <Currency value={rateCardDifferenceRate} />
            </>
          }
          afterTitle="New Rate"
          afterComponent={<Currency value={newData.billingRegularRate} />}
        />
      )}
      {showDeviationWarning && (
        <div style={{ marginBottom: 16 }}>
          <BillingCardDeviationAlert type="extensionOffer" />
        </div>
      )}
      <ExtensionReviewChanges
        beforeTitle="Original Rate"
        beforeComponent={<Currency value={assignment.billingRegularRate} />}
        forwardComponent={
          <>
            <br />
            {differenceRate > 0 ? '+' : ''}
            <Currency value={differenceRate} />
          </>
        }
        afterTitle="New Rate"
        afterComponent={<Currency value={newData.billingRegularRate} />}
      />
      <ExtensionReviewChanges
        beforeTitle="Original Shift Type"
        beforeComponent={assignment.shiftType}
        forwardComponent={<></>}
        afterTitle="New Shift Type"
        afterComponent={newData.shiftType}
      />
      <ExtensionReviewChanges
        beforeTitle="Original Shifts Per Week"
        beforeComponent={assignment.shiftsPerWeek}
        forwardComponent={<></>}
        afterTitle="New Shifts Per Week"
        afterComponent={newData.shiftsPerWeek}
      />
      <ExtensionReviewChanges
        beforeTitle="Original Hours Per Shift"
        beforeComponent={assignment.hoursPerShift}
        forwardComponent={<></>}
        afterTitle="New Hours Per Shift"
        afterComponent={newData.hoursPerShift}
      />
      <ExtensionReviewChanges
        beforeTitle="Original Estimated Budget"
        beforeComponent={<Currency value={initialBudget} />}
        forwardComponent={
          <>
            <br />+<Currency value={budgetIncrement} />
          </>
        }
        afterTitle="New Estimated Budget"
        afterComponent={<Currency value={initialBudget + budgetIncrement} />}
      />
      {!!newData.overtimeThreshold && (
        <ExtensionReviewChanges
          beforeTitle="Original Overtime Threshold"
          beforeComponent={`${assignment.lastAssignmentTerm.overtimeThreshold} hours`}
          forwardComponent={<></>}
          afterTitle="New Overtime Threshold"
          afterComponent={`${newData.overtimeThreshold} hours`}
        />
      )}
      <Row className="changes">
        <Col className="after" span={24}>
          Time Off Request
          <br />
          <strong>{newData.timeoffDescription}</strong>
        </Col>
      </Row>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Extension Reason"
          name="extensionReason"
          rules={[{ required: true, message: 'Please select a reason' }]}
        >
          <Select size="large">
            {EXTENSION_OFFER_REASON_KEYS.map((key) => (
              <Option key={key} value={key}>
                {getExtensionOfferReasonDescription(key)}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
      <style jsx>
        {`
          .ExtensionOfferReview hr {
            border: none;
            border-top: 1px solid ${Colors.steel};
            margin-bottom: 24px;
          }

          .ExtensionOfferReview :global(.changes) {
            text-align: center;
            text-transform: capitalize;
          }

          .ExtensionOfferReview :global(.before),
          .ExtensionOfferReview :global(.delta),
          .ExtensionOfferReview :global(.after) {
            padding: 0.5rem 0;
            margin-bottom: 1rem;
          }

          .ExtensionOfferReview :global(.before) {
            background-color: ${Colors.steelLighter};
          }

          .ExtensionOfferReview :global(.delta) {
            color: ${Colors.grass};
          }

          .ExtensionOfferReview :global(.after) {
            background-color: ${Colors.grassLightest};
          }
        `}
      </style>
    </div>
  );
}
